import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { workspace } from '../api/catalog.js'
import { call, pageItems, pagingQuery, type PagedResult, type PagingFlags } from '../api/client.js'
import { loadConfig, saveConfig } from '../config.js'
import { output, outputRecord } from '../output.js'

interface WorkspaceDto {
  id: string
  name: string
  slug?: string
  description?: string
  [key: string]: unknown
}

export function registerWorkspace(program: Command): void {
  const wsCmd = program.command('workspace').description('Manage workspaces')

  wsCmd
    .command('list')
    .description('List workspaces you can access')
    .option('--page <n>')
    .option('--page-size <n>')
    .option('--order-by <field>')
    .option('--json', 'output JSON')
    .action(async (options: PagingFlags & { json?: boolean }) => {
      const api = await createApiClient()
      const page = await call<PagedResult<WorkspaceDto>>(api, workspace.list, { query: pagingQuery(options) })
      output(pageItems(page), { name: 'Name', slug: 'Slug', id: 'Id' }, options, page)
    })

  wsCmd
    .command('get [workspaceId]')
    .description('Show workspace details (default: configured default workspace)')
    .option('--json', 'output JSON')
    .action(async (workspaceId: string | undefined, options: { json?: boolean }) => {
      const config = await loadConfig()
      const id = workspaceId ?? config.defaultWorkspaceId
      if (!id) throw new Error('No workspace: pass an id or set one with `verentis use workspace <id>`.')
      const api = await createApiClient()
      const result = await call<Record<string, unknown>>(api, workspace.get, { params: { workspaceId: id } })
      outputRecord(result, options)
    })

  wsCmd
    .command('create <name>')
    .description('Create a workspace owned by you')
    .option('--use', 'set the new workspace as the default')
    .action(async (name: string, options: { use?: boolean }) => {
      const api = await createApiClient()
      const id = await call<string>(api, workspace.createMine, { body: { name } })
      console.log(`Workspace '${name}' created (${id}).`)
      if (options.use) {
        const config = await loadConfig()
        config.defaultWorkspaceId = id
        await saveConfig(config)
        console.log('Set as default workspace.')
      }
    })

  wsCmd
    .command('update <workspaceId>')
    .description('Update workspace details')
    .requiredOption('--name <name>', 'workspace name')
    .option('--description <description>')
    .action(async (workspaceId: string, options: { name: string; description?: string }) => {
      const api = await createApiClient()
      await call(api, workspace.update, {
        params: { workspaceId },
        body: { workspaceId, name: options.name, description: options.description ?? null },
      })
      console.log(`Workspace ${workspaceId} updated.`)
    })

  const domainCmd = wsCmd.command('domain').description('Manage workspace domains')

  domainCmd
    .command('list <workspaceId>')
    .description('List domains attached to a workspace')
    .option('--json', 'output JSON')
    .action(async (workspaceId: string, options: { json?: boolean }) => {
      const api = await createApiClient()
      const result = await call<unknown>(api, workspace.listDomains, { params: { workspaceId } })
      const items = pageItems(result as PagedResult<Record<string, unknown>>)
      output(items, { host: 'Host', status: 'Status', id: 'Id' }, options, result)
    })

  domainCmd
    .command('add <workspaceId> <host>')
    .description('Attach a domain to a workspace')
    .action(async (workspaceId: string, host: string) => {
      const api = await createApiClient()
      const id = await call<string>(api, workspace.addDomain, { params: { workspaceId }, body: { workspaceId, host } })
      console.log(`Domain ${host} added${id ? ` (${id})` : ''}.`)
    })

  domainCmd
    .command('remove <workspaceId> <domainId>')
    .description('Remove a domain from a workspace')
    .action(async (workspaceId: string, domainId: string) => {
      const api = await createApiClient()
      await call(api, workspace.removeDomain, { params: { workspaceId, domainId } })
      console.log(`Domain ${domainId} removed.`)
    })

  const inviteCmd = wsCmd.command('invite').description('Manage workspace invitations')

  inviteCmd
    .command('list <workspaceId>')
    .description('List invitations for a workspace')
    .option('--json', 'output JSON')
    .action(async (workspaceId: string, options: { json?: boolean }) => {
      const api = await createApiClient()
      const page = await call<PagedResult<Record<string, unknown>>>(api, workspace.listInvitations, { params: { workspaceId } })
      output(pageItems(page), { emailAddress: 'Email', roleKey: 'Role', status: 'Status', id: 'Id' }, options, page)
    })

  inviteCmd
    .command('create <workspaceId> <email>')
    .description('Invite a user to a workspace')
    .option('--role <roleKey>', 'role to assign on acceptance', 'member')
    .action(async (workspaceId: string, email: string, options: { role: string }) => {
      const api = await createApiClient()
      const id = await call<string>(api, workspace.createInvitation, {
        params: { workspaceId },
        body: { workspaceId, emailAddress: email, roleKey: options.role },
      })
      console.log(`Invitation${id ? ` ${id}` : ''} sent to ${email} (role ${options.role}).`)
    })

  inviteCmd
    .command('resend <workspaceId> <invitationId>')
    .description('Resend a workspace invitation')
    .action(async (workspaceId: string, invitationId: string) => {
      const api = await createApiClient()
      await call(api, workspace.resendInvitation, { params: { workspaceId, invitationId }, body: { workspaceId, invitationId } })
      console.log(`Invitation ${invitationId} resent.`)
    })

  inviteCmd
    .command('revoke <workspaceId> <invitationId>')
    .description('Revoke a workspace invitation')
    .action(async (workspaceId: string, invitationId: string) => {
      const api = await createApiClient()
      await call(api, workspace.revokeInvitation, { params: { workspaceId, invitationId } })
      console.log(`Invitation ${invitationId} revoked.`)
    })

  inviteCmd
    .command('accept <code>')
    .description('Accept a workspace invitation code as the current user')
    .action(async (code: string) => {
      const api = await createApiClient()
      await call(api, workspace.acceptInvitation, { body: { code } })
      console.log('Invitation accepted.')
    })
}
